// src/views/Shop/Shop.js
import React, { useState, useEffect } from "react";
import productAll from "../../api/productAll";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import bagIcon from "./../../assets/bagicon.png";
import "./Shop.css";
import { loadProductsWithLangFallback } from "../../utils/productLoader";
import { useTranslation } from "react-i18next";
import { useCurrency } from "./../../components/PriceFormat/PriceFormat";
import { useSEO } from "../../context/SEOContext";

// 🔽 yeni fiyat yardımcıları
import { pickDisplayPrice, formatMoney } from "../../utils/pricePicker";

const Shop = () => {
  const { t, i18n } = useTranslation();
  const setSeo = useSEO();

  useEffect(() => {
    setSeo({
      title: t("shop.meta.title"),
      description: t("shop.meta.description"),
      keywords: t("shop.meta.keywords"),
      favicon: "/faviconnew.png",
      ogUrl: "https://mukemel.com/shop",
    });
  }, [setSeo, t]);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [wishlistItems, setWishlistItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All Products");

  // Para birimi durumu
  const { currency, conversionRate } = useCurrency();

  // “Daha fazla yükle”
  const [productsToShow, setProductsToShow] = useState(8);
  const handleLoadMore = () => setProductsToShow((prev) => prev + 8);

  // Ürünleri çek + lokalizasyonu birleştir
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const enData = await productAll();
        const localizedData = await loadProductsWithLangFallback(i18n.language);

        const merged = enData.map((item) => {
          const localized = localizedData.find(
            (p) => p.stock_code === item.stock_code
          );
          return { ...item, ...(localized || {}) };
        });

        setProducts(merged);
        extractCategories(merged);
      } catch (err) {
        console.error("Fetch error", err);
        setError("Error fetching products");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [i18n.language]);

  // Kategorileri çıkar
  const extractCategories = (list) => {
    const all = [];
    list.forEach((p) => {
      (p.category || []).forEach((c) => {
        if (!all.includes(c)) all.push(c);
      });
    });
    setCategories(all);
  };

  // Kategori filtresi
  const filterProductsByCategory = (category) => {
    if (category === "All Products") return products;
    return products.filter((p) => (p.category || []).includes(category));
  };

  // Favoriler senkronizasyonu
  useEffect(() => {
    const syncWishlist = () => {
      const stored = JSON.parse(localStorage.getItem("wishlistItems")) || [];
      setWishlistItems(stored);
    };
    syncWishlist();
    window.addEventListener("storage", syncWishlist);
    window.addEventListener("wishlistUpdated", syncWishlist);
    return () => {
      window.removeEventListener("storage", syncWishlist);
      window.removeEventListener("wishlistUpdated", syncWishlist);
    };
  }, []);

  const handleWishlistClick = (productId) => (e) => {
    e.preventDefault();
    let updated;
    if (wishlistItems.includes(productId)) {
      updated = wishlistItems.filter((id) => id !== productId);
      Swal.fire({
        icon: "success",
        title: t("RemovedWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
        background: "#fff",
        iconColor: "#dc3545",
      });
    } else {
      updated = [...wishlistItems, productId];
      Swal.fire({
        icon: "success",
        title: t("AddWishlistItems"),
        toast: true,
        position: "bottom-end",
        showConfirmButton: false,
        timer: 2000,
      });
    }
    setWishlistItems(updated);
    localStorage.setItem("wishlistItems", JSON.stringify(updated));
    window.dispatchEvent(new Event("wishlistUpdated"));
  };

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("wishlistItems")) || [];
    setWishlistItems(saved);
  }, []);

  const handleCategoryClick = (category) => {
    if (category !== selectedCategory) setSelectedCategory(category);
  };

  // 🔽 Seçili para biriminde ürünün “tekil birim fiyatını” bul (kategori sayfasındakiyle aynı mantık)
  const getUnitCurr = (p) => {
    const picked = pickShopPrice(p, currency); // { amount, curr } | null
    // Eski tek-price (TRY string) fallback
    const legacyTry = Number(
      String(p.price ?? 0)
        .replace(/\./g, "")
        .replace(",", ".")
    );
    if (picked) return picked.amount;
    if (Number.isFinite(legacyTry)) return legacyTry / conversionRate;
    return 0;
  };

  // 🔽 Sıralama: seçili para biriminde
  const [sortOption, setSortOption] = useState("");
  const getSortedProducts = () => {
    const filtered = filterProductsByCategory(selectedCategory);
    if (sortOption === "asc") {
      return [...filtered].sort((a, b) => getUnitCurr(a) - getUnitCurr(b));
    }
    if (sortOption === "desc") {
      return [...filtered].sort((a, b) => getUnitCurr(b) - getUnitCurr(a));
    }
    return filtered;
  };

  // Perfume kontrolü + manual_prices önceliği
  const pickShopPrice = (p, curr) => {
    if (!p) return null;

    // kategori "Perfume" mi?
    const isPerfume = Array.isArray(p.category)
      ? p.category.includes("Perfume")
      : typeof p.category === "string" &&
        p.category.toLowerCase().includes("perfume");

    // para birimini normalize et
    let key = String(curr || "TRY").toUpperCase();
    if (key === "TL") key = "TRY";

    // parfümse manual_prices öncelikli
    if (isPerfume && p.manual_prices) {
      const val = p.manual_prices?.[key];
      if (val !== undefined && val !== null && !Number.isNaN(Number(val))) {
        return { amount: Number(val), curr: key };
      }
    }

    // diğer tüm durumlarda mevcut helper
    return pickDisplayPrice(p, key); // {amount, curr} | null
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center">
            <div className="alert alert-warning" role="alert">
              Your shop is loading, please wait.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6 text-center">
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="container-fluid border-bottom">
        <div className="row py-3 pe-3">
          <div className="col-lg-6 col-md-6 col-sm-6 col-6">
            <div className="dropdown">
              <button
                className="btn dropdown-toggle btnfox"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
              >
                {t(selectedCategory) || selectedCategory}
              </button>

              <ul
                className="dropdown-menu mt-3 rounded-0"
                aria-labelledby="dropdownMenuButton"
              >
                <li>
                  <button
                    className="dropdown-item"
                    onClick={() => setSelectedCategory("All Products")}
                    disabled={selectedCategory === "All Products"}
                  >
                    {t("All Products") || "All Products"}
                    {selectedCategory === "All Products" && (
                      <span className="dot mx-2"></span>
                    )}
                  </button>
                </li>

                {categories.map((category, index) => (
                  <li key={index}>
                    <button
                      className="dropdown-item"
                      onClick={() => handleCategoryClick(category)}
                      disabled={selectedCategory === category}
                    >
                      {t(category) || category}
                      {selectedCategory === category && (
                        <span className="dot mx-2"></span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="col-lg-6 col-md-6 col-sm-6 col-6 text-end">
            <select
              className="form-select w-auto d-inline-block ms-3 wmobil"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="">{t("Sort by")}</option>
              <option value="asc">{t("Price: Low to High")}</option>
              <option value="desc">{t("Price: High to Low")}</option>
            </select>
          </div>
        </div>
      </div>

      <div className="container-fluid">
        <div className="row">
          {getSortedProducts()
            .slice(0, productsToShow)
            .map((product) => {
              const picked = pickShopPrice(product, currency); // {amount, curr} | null
              const unit = getUnitCurr(product); // sayısal
              const dispCurr = (picked && picked.curr) || currency;

              return (
                <div
                  key={product.id}
                  className="col-12 col-md-6 col-lg-3 pe-0 px-0 fade-in"
                >
                  <div className="card cardbg border-0 rounded-0">
                    <Link
                      onClick={handleWishlistClick(product.id)}
                      className="wishlist-button"
                    >
                      <i
                        className={`${
                          wishlistItems.includes(product.id)
                            ? "fas fa-heart text-white"
                            : "far fa-heart text-white"
                        } fa-lg wishlist-icon`}
                      />
                    </Link>

                    <div
                      id={`product-carousel-${product.id}`}
                      className="carousel slide"
                      data-bs-ride="carousel"
                    >
                      <div className="carousel-inner">
                        <Link to={`/product/${product.id}`}>
                          {(product.default_images || []).map((img, index) => (
                            <div
                              className={`carousel-item ${
                                index === 0 ? "active" : ""
                              }`}
                              key={index}
                            >
                              <img
                                src={img}
                                className="d-block w-100"
                                alt={product.name}
                              />
                            </div>
                          ))}
                        </Link>
                      </div>

                      <button
                        className="carousel-control-prev"
                        type="button"
                        data-bs-target={`#product-carousel-${product.id}`}
                        data-bs-slide="prev"
                      >
                        <i className="fa-solid fa-angle-left carousel-control-prev-icon" />
                      </button>
                      <button
                        className="carousel-control-next"
                        type="button"
                        data-bs-target={`#product-carousel-${product.id}`}
                        data-bs-slide="next"
                      >
                        <i className="fa-solid fa-chevron-right carousel-control-next-icon" />
                      </button>
                    </div>

                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex flex-column">
                          <h5 className="mb-0 text-dark">{product.name}</h5>

                          {String(product.availability || "")
                            .toLowerCase()
                            .includes("in stock") ? (
                            <p className="fw-normal fs-6 mt-1">
                              {formatMoney(unit, dispCurr)}
                            </p>
                          ) : (
                            <p className="fw-bold fw-14 mt-1 text-danger">
                              Out of Stock!
                            </p>
                          )}
                        </div>

                        <Link
                          to={`/product/${product.id}`}
                          className="btn custom-btn d-flex align-items-center justify-content-center"
                        >
                          <img
                            src={bagIcon}
                            width={15}
                            alt="Bag Icon"
                            className="icon"
                          />
                          <span className="text ms-2">{t("Shop")}</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {productsToShow < filterProductsByCategory(selectedCategory).length && (
          <div className="text-center my-5">
            <button onClick={handleLoadMore} className="btn fs-2 px-4 py-2">
              {t("Show More")}
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default Shop;
